import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { apiErrorText, useListFsnbEditionsQuery, type FsnbEdition } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { NormBaseEditions } from './NormBaseEditions';
import { NormBaseSearch } from './NormBaseSearch';
import { editionStatusLabel, fmtDate } from './normBaseLabels';

/** Пока идёт разбор архива, список редакций опрашивается — статус меняет
 *  фоновая задача, а не действие на этой странице. */
const POLL_MS = 4000;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const Current = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 14px;

  & .muted {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 13px;
  }
`;

const Banner = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.status.warning}55;
  background: ${({ theme }) => theme.colors.status.warning}12;
  font-size: 13px;
  line-height: 1.5;

  & .text {
    flex: 1 1 320px;
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Tab = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border: none;
  border-bottom: 2px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : 'transparent')};
  margin-bottom: -1px;
  background: transparent;
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.text.secondary)};
  font: inherit;
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  cursor: pointer;
`;

const Err = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.status.danger};
`;

type TabKey = 'search' | 'editions';

/** Какую редакцию раскрыть во вкладке «Редакции», если в адресе её нет:
 *  сначала ту, что требует внимания. */
function defaultSelected(items: FsnbEdition[]): string | null {
  for (const status of ['pending_review', 'importing', 'failed', 'active'] as const) {
    const e = items.find((x) => x.status === status);
    if (e) return e.id;
  }
  return items[0]?.id ?? null;
}

/** Раздел «Нормативная база»: поиск по действующей редакции ФСНБ-2022 и
 *  редакции (загрузка дополнения, проверка изменений, ввод в действие). */
export function NormBase() {
  const [params, setParams] = useSearchParams();
  const tab: TabKey = params.get('tab') === 'editions' ? 'editions' : 'search';
  const [pollMs, setPollMs] = useState(0);
  const q = useListFsnbEditionsQuery(undefined, { pollingInterval: pollMs });
  const items = q.data?.items ?? [];
  const importing = items.some((e) => e.status === 'importing');
  useEffect(() => setPollMs(importing ? POLL_MS : 0), [importing]);

  const active = items.find((e) => e.status === 'active') ?? null;
  const pending = items.filter((e) => e.status === 'pending_review');
  const selectedId = params.get('edition') ?? defaultSelected(items);

  function go(next: TabKey, editionId?: string | null) {
    const p = new URLSearchParams(params);
    if (next === 'search') p.delete('tab');
    else p.set('tab', next);
    if (editionId !== undefined) {
      if (editionId) p.set('edition', editionId);
      else p.delete('edition');
    }
    setParams(p, { replace: true });
  }

  return (
    <Wrap>
      <Current>
        {active ? (
          <>
            <span>
              Действует: <strong>{active.title}</strong>
            </span>
            <span className="muted">
              {active.decree ?? active.edition}
              {active.activated_at ? ` · с ${fmtDate(active.activated_at)}` : ''}
            </span>
          </>
        ) : (
          q.data && (
            <span className="muted">Действующей редакции нет — загрузите архив ФСНБ-2022.</span>
          )
        )}
      </Current>
      {q.error && <Err>{apiErrorText(q.error, 'Не удалось загрузить список редакций.')}</Err>}
      {pending.map((e) => (
        <Banner key={e.id}>
          <span className="text">
            <strong>{e.title}</strong> загружена и ждёт проверки. Расчёты идут по действующей
            редакции, пока новую не введут в действие.
          </span>
          <Button
            type="button"
            $variant="secondary"
            $size="sm"
            onClick={() => go('editions', e.id)}
          >
            Посмотреть изменения
          </Button>
        </Banner>
      ))}

      <Tabs role="tablist" aria-label="Разделы нормативной базы">
        <Tab
          type="button"
          role="tab"
          aria-selected={tab === 'search'}
          $active={tab === 'search'}
          onClick={() => go('search')}
        >
          Нормы, ресурсы, коэффициенты
        </Tab>
        <Tab
          type="button"
          role="tab"
          aria-selected={tab === 'editions'}
          $active={tab === 'editions'}
          onClick={() => go('editions')}
        >
          Редакции
          {(pending.length > 0 || importing) && (
            <Badge $tone={importing ? 'info' : 'warning'}>
              {importing ? editionStatusLabel('importing').toLowerCase() : pending.length}
            </Badge>
          )}
        </Tab>
      </Tabs>

      {tab === 'search' && (
        <NormBaseSearch editions={items} activeEdition={q.data?.active_edition ?? null} />
      )}
      {tab === 'editions' && (
        <NormBaseEditions
          data={q.data}
          selectedId={selectedId}
          onSelect={(id) => go('editions', id)}
        />
      )}
    </Wrap>
  );
}
